import PropTypes from "prop-types";
import { Button, Collapse } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import InsuranceCoverageCard from "./InsuranceCoverageCard";
import DocumentRowIcon from "../documents/DocumentRowIcon";
import RentalAgreementRow from "../documents/RentalAgreementRow";
import { formatReservationDate } from "../utils";

const InsuranceDocumentsCard = ({
    resDetails,
    insurancePolicyData,
    isInsuranceLoading,
    onRefresh,
    t,
}) => {
    const history = useHistory();
    const hasInsurance = Boolean(resDetails?.insuranceType);
    const agreementSignedDate = resDetails?.agreementDocUrl
        ? formatReservationDate(resDetails?.updatedAt || resDetails?.createdAt)
        : null;

    const insuranceSummary = hasInsurance
        ? resDetails.insuranceType.charAt(0).toUpperCase() + resDetails.insuranceType.slice(1)
        : t("upcomingDetails.noCoverageSelected");

    const insurancePanel = {
        key: "insurance",
        label: (
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <DocumentRowIcon icon={<SafetyCertificateOutlined className="text-lg" />} tone="primary" />
                <div className="min-w-0">
                    <p className="fontSemi text-sm text-[#2F2F46]">{t("upcomingDetails.insuranceCoverage")}</p>
                    <p className="fontRegular mt-0.5 text-sm text-[#7f8c8d]">{insuranceSummary}</p>
                </div>
            </div>
        ),
        extra: !hasInsurance ? (
            <Button
                type="link"
                className="!h-auto !px-0 !font-semibold !text-[#5D5DF6]"
                onClick={(event) => {
                    event.stopPropagation();
                    if (resDetails?.id) {
                        history.push(`/reservations/${resDetails.id}/add-coverage`);
                    }
                }}>
                {t("upcomingDetails.addCoverage")}
            </Button>
        ) : null,
        children: hasInsurance ? (
            <InsuranceCoverageCard
                resDetails={resDetails}
                insurancePolicyData={insurancePolicyData}
                t={t}
                isInsuranceLoading={isInsuranceLoading}
                onRefresh={onRefresh}
                embedded
                hideHeader
                hideRentalAgreement
            />
        ) : (
            <p className="fontRegular text-sm text-[#7f8c8d]">{t("upcomingDetails.noCoverageSelected")}</p>
        ),
    };

    return (
        <div className="rounded-2xl border border-[#f0f0f0] bg-white p-4 lg:p-5">
            <h2 className="fontSemi mb-4 text-base text-[#2F2F46]">{t("upcomingDetails.insuranceAndDocuments")}</h2>

            <div className="flex flex-col gap-3">
                <div className="overflow-hidden rounded-xl border border-[#f0f0f0] bg-white">
                    <Collapse
                        accordion
                        bordered={false}
                        expandIconPosition="end"
                        defaultActiveKey={[]}
                        className="insurance-documents-accordion !bg-white [&_.ant-collapse-content-box]:!px-4 [&_.ant-collapse-content-box]:!pb-4 [&_.ant-collapse-expand-icon]:!text-[#9b9ba5] [&_.ant-collapse-header]:!items-center [&_.ant-collapse-header]:!gap-3 [&_.ant-collapse-header]:!px-4 [&_.ant-collapse-header]:!py-4 [&_.ant-collapse-item]:!border-none"
                        items={[insurancePanel]}
                    />
                </div>

                <RentalAgreementRow
                    resDetails={resDetails}
                    t={t}
                    onRefresh={onRefresh}
                    signedDate={agreementSignedDate}
                />
            </div>
        </div>
    );
};

InsuranceDocumentsCard.propTypes = {
    resDetails: PropTypes.object,
    insurancePolicyData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    isInsuranceLoading: PropTypes.bool,
    onRefresh: PropTypes.func,
    t: PropTypes.func.isRequired,
};

export default InsuranceDocumentsCard;
